import {
  Amount,
  BigIntMath,
  Fraction,
  Percent,
  Rounding,
  Token,
  formatPercent,
  formatUSD,
} from "@eulerxyz/core-sdk"
import { BatchResultStatus } from "@eulerxyz/evc-sdk"
import { createSelector } from "@reduxjs/toolkit"
import {
  type Address,
  formatUnits,
  getAddress,
  isAddressEqual,
  zeroAddress,
} from "viem"
import type { GetAccountInfoData, GetVaultData, SimulationData } from "../query"
import {
  HEALTH_SCORE_SCALE,
  formatBigIntToPrice,
  formatTtl,
  nonNullable,
} from "../utils"
import {
  selectConvertUnitOfAccountAmountToPrice,
  selectPrice,
  selectPriceExtended,
} from "./pricing"

export const HEALTH_SCORE_PRECISION = 2
export const HEALTH_SCORE_ROUNDING = Rounding.ROUND_DOWN
export const MAX_DISPLAYED_HEALTH = 100

export const ZERO_PERCENT = "0.00%"

//TODO: extract into the en.json?
const QUERY_FAILED = "Query failed"
const TYPE_ERROR = "error"
const TYPE_INFO = "info"

type FieldValue = string | number
type FieldType = "warning" | "error" | "info" | "success"
type FormattedFieldResult = {
  from?: FieldValue
  fromType?: FieldType
  to?: FieldValue
  toType?: FieldType
}

export const selectHealthScoreData = createSelector(
  [(simulation: SimulationData) => simulation],
  (simulation) => ({
    before: simulation?.healthScoreBefore,
    after: simulation?.healthScoreAfter,
  }),
)

export const selectTtlData = createSelector(
  [(simulation: SimulationData) => simulation],
  (simulation) => ({
    before: simulation?.ttlBefore,
    after: simulation?.ttlAfter,
  }),
)

export const selectAccountHealthScore = createSelector(
  [selectHealthScoreData, (_, address: Address) => address],
  ({ before, after }, address) => ({
    before: before?.get(getAddress(address)),
    after: after?.get(getAddress(address)),
  }),
)

export const selectAccountHealthScorePercentage = createSelector(
  [selectAccountHealthScore],
  ({ before, after }) => ({
    before: before
      ? new Fraction(before)
          .divide(HEALTH_SCORE_SCALE)
          .toFixed(HEALTH_SCORE_PRECISION, {}, HEALTH_SCORE_ROUNDING)
      : undefined,
    after: after
      ? new Fraction(after)
          .divide(HEALTH_SCORE_SCALE)
          .toFixed(HEALTH_SCORE_PRECISION, {}, HEALTH_SCORE_ROUNDING)
      : undefined,
  }),
)

export const selectAccountHealthScorePercentageWithCapText = createSelector(
  [selectAccountHealthScorePercentage],
  ({ before, after }) => ({ before, after }),
)

export const selectAccountHealthScorePercentageWithCapTextFormatted =
  createSelector(
    [selectAccountHealthScorePercentageWithCapText],
    ({ before, after }) => {
      const getTypeAndValue = (value: string | undefined) => {
        if (!value) return { value: undefined, type: undefined }
        const numValue = Number.parseFloat(value?.replace(",", ""))
        if (numValue > MAX_DISPLAYED_HEALTH)
          return { value: `>${MAX_DISPLAYED_HEALTH}`, type: "success" as const }
        if (numValue > 1.5) return { value: value, type: "success" as const }
        if (numValue > 1.2) return { value: value, type: "info" as const }
        if (numValue > 1.05) return { value: value, type: "warning" as const }
        return { value: value, type: "error" as const }
      }

      const beforeResult = getTypeAndValue(before)
      const afterResult = getTypeAndValue(after)

      return {
        from: beforeResult.value,
        fromType: beforeResult.type,
        to: afterResult.value,
        toType: afterResult.type,
      }
    },
  )

export const selectAccountTtl = createSelector(
  [selectTtlData, (_, address: Address) => address],
  ({ before, after }, address) => ({
    before: before?.get(getAddress(address)),
    after: after?.get(getAddress(address)),
  }),
)

export const selectAccountTtlWithCapTextAndColor = createSelector(
  [selectAccountTtl],
  ({ before, after }) => ({
    before: formatTtl(before),
    after: formatTtl(after),
  }),
)

export const selectAccountHealth = createSelector(
  [selectAccountHealthScore, selectAccountTtl],
  (healthScore, ttl) => ({
    healthScore,
    ttl,
  }),
)

export const selectCollateralsData = createSelector(
  [(simulation: SimulationData) => simulation],
  (simulation) => ({
    before: simulation?.collateralsBefore,
    after: simulation?.collateralsAfter,
  }),
)

export const selectVaultCheckBeforeAfterAssets = createSelector(
  [
    (simulation: SimulationData) => simulation,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
  ],
  (simulation, allUnitOfAccountPrices) => {
    const vault = simulation?.currentVaultBefore?.vault
    if (!vault)
      return { before: undefined, after: undefined, error: "No vault" }
    const vaultInfo = simulation.vaultsAfter?.get(getAddress(vault))
    if (!vaultInfo)
      return { before: undefined, after: undefined, error: "No vault info" }
    const vaultPrice = allUnitOfAccountPrices?.get(
      getAddress(
        vaultInfo.unitOfAccount === zeroAddress
          ? vaultInfo.backupAssetPriceInfo.unitOfAccount
          : vaultInfo.unitOfAccount,
      ),
    )
    if (!vaultPrice || vaultPrice === 0n)
      return { before: undefined, after: undefined, error: "No vault price" }

    return {
      before: simulation?.currentVaultBefore?.assets,
      after: simulation?.currentVaultAfter?.assets,
      vaultPrice,
      vaultInfo,
    }
  },
)

export const selectVaultCheckBeforeAfterAssetsV2 = createSelector(
  [
    (simulation: SimulationData) => simulation,
    (_, vault: Address) => vault,
    (_, __, allUnitOfAccountPrices: Map<Address, bigint>) =>
      allUnitOfAccountPrices,
  ],
  (simulation, _vault, allUnitOfAccountPrices) => {
    if (!simulation)
      return { before: undefined, after: undefined, error: "No Simulation" }

    const vault = simulation.accountInfosBefore.find(
      (info) =>
        info &&
        getAddress(info.vaultAccountInfo.vault) === getAddress(_vault) &&
        getAddress(info.vaultAccountInfo.account) ===
          getAddress(simulation.currentAccount || zeroAddress),
    )?.vaultAccountInfo

    const vaultAfter = simulation.accountInfosAfter.find(
      (info) =>
        info &&
        getAddress(info.vaultAccountInfo.vault) === getAddress(_vault) &&
        getAddress(info.vaultAccountInfo.account) ===
          getAddress(simulation.currentAccount || zeroAddress),
    )?.vaultAccountInfo

    const vaultInfo = simulation.vaultsAfter?.get(getAddress(_vault))

    if (!vault || !vaultAfter)
      return { before: undefined, after: undefined, error: "No vault" }
    if (!vaultInfo)
      return { before: undefined, after: undefined, error: "No vault info" }
    const vaultPrice = allUnitOfAccountPrices?.get(
      getAddress(
        vaultInfo.unitOfAccount === zeroAddress
          ? vaultInfo.backupAssetPriceInfo.unitOfAccount
          : vaultInfo.unitOfAccount,
      ),
    )
    if (!vaultPrice || vaultPrice === 0n)
      return { before: undefined, after: undefined, error: "No vault price" }

    return {
      before: vault?.assets,
      after: vaultAfter?.assets,
      vaultPrice,
      vaultInfo,
    }
  },
)

export const selectVaultCheckBeforeAfterBorrowed = createSelector(
  [
    (simulation: SimulationData) => simulation,
    (_, vault: Address) => vault,
    (_, __, allUnitOfAccountPrices: Map<Address, bigint>) =>
      allUnitOfAccountPrices,
  ],
  (simulation, _vault, allUnitOfAccountPrices) => {
    if (!simulation)
      return { before: undefined, after: undefined, error: "No Simulation" }
    const vault = simulation.accountInfosBefore.find(
      (info) =>
        info &&
        getAddress(info.vaultAccountInfo.vault) === getAddress(_vault) &&
        getAddress(info.vaultAccountInfo.account) ===
          getAddress(simulation.currentAccount || zeroAddress),
    )?.vaultAccountInfo

    const vaultAfter = simulation.accountInfosAfter.find(
      (info) =>
        info &&
        getAddress(info.vaultAccountInfo.vault) === getAddress(_vault) &&
        getAddress(info.vaultAccountInfo.account) ===
          getAddress(simulation.currentAccount || zeroAddress),
    )?.vaultAccountInfo

    const vaultInfo = simulation.vaultsAfter?.get(getAddress(_vault))

    if (!vault || !vaultAfter)
      return { before: undefined, after: undefined, error: "No vault" }
    if (!vaultInfo)
      return { before: undefined, after: undefined, error: "No vault info" }
    const vaultPrice = allUnitOfAccountPrices?.get(
      getAddress(
        vaultInfo.unitOfAccount === zeroAddress
          ? vaultInfo.backupAssetPriceInfo.unitOfAccount
          : vaultInfo.unitOfAccount,
      ),
    )
    if (!vaultPrice || vaultPrice === 0n)
      return { before: undefined, after: undefined, error: "No vault price" }

    return {
      before: vault?.borrowed,
      after: vaultAfter?.borrowed,
      vaultPrice,
      vaultInfo,
    }
  },
)

export const selectBalances = createSelector(
  [
    (vaultAccountInfo?: GetAccountInfoData) =>
      vaultAccountInfo?.vaultAccountInfo,
    (_, vaultData?: GetVaultData) => vaultData,
  ],
  (vaultAccountInfo, vaultInfoFull) => {
    if (!vaultAccountInfo || !vaultInfoFull) return {}
    const vault = vaultAccountInfo.vault
    const assetsAccount = vaultAccountInfo.assetsAccount
    const repayUserMax = vaultAccountInfo.borrowed
    const assetDecimals = vaultInfoFull.assetDecimals
    const supplyCap = vaultInfoFull.supplyCap
    const totalAssets = vaultInfoFull.totalAssets
    const totalCash = vaultInfoFull.totalCash
    const totalBorrowed = vaultInfoFull.totalBorrowed
    const borrowCap = vaultInfoFull.borrowCap
    const depositUserMax = BigIntMath.min(
      assetsAccount,
      supplyCap > totalAssets ? supplyCap - totalAssets : 0n,
    )

    vaultInfoFull?.collateralLTVInfo

    const withdrawUserMax = BigIntMath.min(vaultAccountInfo.assets, totalCash)
    const transferMax = BigIntMath.min(vaultAccountInfo.assets, totalCash)
    const borrowVaultMax = BigIntMath.min(
      totalCash,
      borrowCap > totalBorrowed ? borrowCap - totalBorrowed : 0n,
    )
    const borrowMax = calculateMaxBorrowAmountInUnitOfAccount(
      vaultInfoFull,
      vaultAccountInfo,
    )

    return {
      depositUserMax,
      withdrawUserMax,
      transferMax,
      borrowCap,
      totalCash,
      borrowMax,
      borrowVaultMax,
      assetsAccount,
      repayUserMax,
      assetDecimals,
      vault,
    }
  },
)

export const selectBalancesBefore = createSelector(
  [(simulation: SimulationData) => simulation],
  (simulation) => {
    const vaultAccountInfo = simulation.currentVaultBefore
    if (!vaultAccountInfo) return {}
    const vault = vaultAccountInfo.vault
    const vaultInfoFull = simulation.vaultsBefore?.get(getAddress(vault))
    if (!vaultInfoFull) return {}

    return selectBalances(vaultAccountInfo, vaultInfoFull)
  },
)

interface CollateralInfo {
  vaultSymbol: string
  vault: Address
  assetSymbol: string
  assetDecimals: bigint
  rawAmount: bigint
  usdPrice?: string
  usdPriceNumber: number
  liquidationLTVFraction: number
  borrowingLTVFraction: number
  riskAdjustedUsdPriceLiquidation: number
  riskAdjustedUsdPriceLiquidationFormatted: string
  riskAdjustedUsdPriceBorrowing: number
  riskAdjustedUsdPriceBorrowingFormatted: string
  oraclePriceNumber: number
  oraclePriceBigInt: bigint | null
}

export type CollateralsForLiabilityCurrentVault = ReturnType<
  typeof selectCollateralsForLiabilityCurrentVault
>

export const selectCollateralsForLiabilityCurrentVault = createSelector(
  [
    (state: SimulationData) => state.currentVaultBefore,
    (state: SimulationData) => state.currentAccountVaultAccountInfoBefore,
    (state: SimulationData) => state.vaultsBefore,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
  ],
  (
    liabilityVault,
    currentAccountVaultAccountInfo,
    vaultsBefore,
    allUnitOfAccountPrices,
  ) => {
    const liabilityVaultCollaterals = vaultsBefore
      .get(getAddress(liabilityVault?.vault || zeroAddress))
      ?.collateralLTVInfo.map((collateral) => ({
        collateral: collateral.collateral,
        borrowLTV: collateral.borrowLTV,
        liquidationLTV: collateral.liquidationLTV,
      }))

    const collateralVaultsBackingLiability: { vault: Address }[] = []
    let totalCollateralValueBackingLiability = 0
    let totalRiskAdjustedCollateralValueBackingLiabilityByLiquidationLTV = 0
    let totalRiskAdjustedCollateralValueBackingLiabilityByBorrowLTV = 0
    const collateralInfo: Record<Address, CollateralInfo> = {}

    liabilityVaultCollaterals?.forEach(
      ({ collateral, liquidationLTV, borrowLTV }) => {
        const vaultAccountInfo = currentAccountVaultAccountInfo.get(
          getAddress(collateral),
        )
        if (!vaultAccountInfo) return // if we don't have that in the current account skip
        const vaultData = vaultsBefore.get(getAddress(collateral))
        if (!vaultData) return
        collateralVaultsBackingLiability.push({ vault: collateral })

        const priceCalculated = selectPriceExtended(
          vaultAccountInfo.assets,
          vaultData,
          allUnitOfAccountPrices,
        )

        if (!priceCalculated) return
        // there must be a bug in adding prices together
        totalCollateralValueBackingLiability +=
          priceCalculated?.amountOutUSDFullScaleNumber || 0

        const usdPriceNumber = Number(
          priceCalculated.amountOutUSDPrice?.toSignificant(4),
        )
        const borrowingLTVFraction = Number(borrowLTV) / 10_000
        const liquidationLTVFraction = Number(liquidationLTV) / 10_000
        const riskAdjustedUsdPriceLiquidation =
          usdPriceNumber * liquidationLTVFraction
        const riskAdjustedUsdPriceBorrowing =
          usdPriceNumber * borrowingLTVFraction
        totalRiskAdjustedCollateralValueBackingLiabilityByLiquidationLTV +=
          riskAdjustedUsdPriceLiquidation
        totalRiskAdjustedCollateralValueBackingLiabilityByBorrowLTV +=
          riskAdjustedUsdPriceBorrowing

        collateralInfo[collateral] = {
          vaultSymbol: vaultData.vaultSymbol,
          vault: vaultData.vault,
          assetSymbol: vaultData.assetSymbol,
          assetDecimals: vaultData.assetDecimals,
          rawAmount: vaultAccountInfo.assets,
          usdPrice: priceCalculated.amountOutUSDPrice?.toSignificant(4),
          usdPriceNumber: usdPriceNumber,
          borrowingLTVFraction,
          liquidationLTVFraction,
          riskAdjustedUsdPriceLiquidation: riskAdjustedUsdPriceLiquidation,
          riskAdjustedUsdPriceLiquidationFormatted:
            riskAdjustedUsdPriceLiquidation.toFixed(2),
          riskAdjustedUsdPriceBorrowing: riskAdjustedUsdPriceBorrowing,
          riskAdjustedUsdPriceBorrowingFormatted:
            riskAdjustedUsdPriceBorrowing.toFixed(2),
          oraclePriceNumber: Number(priceCalculated.oraclePriceNumber),
          oraclePriceBigInt: priceCalculated.oraclePriceBigInt,
        }
      },
    )

    let allCollateralValues = 0
    const allCollateralsVaultsInfo: Record<Address, CollateralInfo> = {}
    const allCollateralsVaults: { vault: Address }[] = []
    currentAccountVaultAccountInfo.forEach((vaultInfo, vault) => {
      if (!vaultInfo.isCollateral || vaultInfo.assets === 0n) return
      allCollateralsVaults.push({ vault: vault })
      const vaultData = vaultsBefore.get(getAddress(vault))
      if (!vaultData) return

      const priceCalculated = selectPriceExtended(
        vaultInfo.assets,
        vaultData,
        allUnitOfAccountPrices,
      )
      if (!priceCalculated) return
      allCollateralsVaultsInfo[vault] = {
        vault: vault,
        vaultSymbol: vaultData.vaultSymbol,
        rawAmount: vaultInfo.assets,
        assetDecimals: vaultData.assetDecimals,
        assetSymbol: vaultData.assetSymbol,
        usdPrice: priceCalculated.amountOutUSDPrice?.toSignificant(4),
        usdPriceNumber: Number(
          priceCalculated.amountOutUSDPrice?.toSignificant(4),
        ),
        liquidationLTVFraction: 0,
        borrowingLTVFraction: 0,
        riskAdjustedUsdPriceBorrowing: 0,
        riskAdjustedUsdPriceBorrowingFormatted: "0",
        riskAdjustedUsdPriceLiquidation: 0,
        riskAdjustedUsdPriceLiquidationFormatted: "0",
        oraclePriceNumber: Number(priceCalculated.oraclePriceNumber),
        oraclePriceBigInt: priceCalculated.oraclePriceBigInt,
      }

      allCollateralValues += priceCalculated.amountOutUSDFullScaleNumber || 0
    })

    return {
      collateralVaults: collateralVaultsBackingLiability,
      allCollateralsVaults,
      allCollateralsVaultsInfo,
      allCollateralValues,
      totalCollateralValueBackingLiability,
      totalRiskAdjustedCollateralValueBackingLiabilityByLiquidationLTV,
      totalRiskAdjustedCollateralValueBackingLiabilityByBorrowLTV,
      collateralInfo,
      formattedCollateralValueBackingLiability: formatUSD(
        totalCollateralValueBackingLiability,
      ),
      formattedRiskAdjustedCollateralValueBackingLiabilityByLiquidationLTV:
        formatUSD(
          totalRiskAdjustedCollateralValueBackingLiabilityByLiquidationLTV,
        ),
      formattedRiskAdjustedCollateralValueBackingLiabilityByBorrowLTV:
        formatUSD(totalRiskAdjustedCollateralValueBackingLiabilityByBorrowLTV),
      formattedAllCollateralValues: formatUSD(allCollateralValues),
    }
  },
)

export interface CollateralValueInfo {
  unitOfAccount?: bigint
  amount: Amount<Token> | null
  number: number | null
  formatted?: string | null
  formattedUSD?: string | null
  collateralSymbol?: string | null
}

export interface SingleCollateralValueInfo {
  collateral: Address
  value: CollateralValueInfo
}

/**
 * Creates a CollateralValueInfo object from unitOfAccount and amount.
 * @param unitOfAccount The unit of account as a bigint
 * @param amount The amount as an Amount<Token> object
 * @returns CollateralValueInfo object
 */
export const createCollateralValueInfo = (
  amount: Amount<Token> | null,
  unitOfAccount = 0n,
  collateralSymbol?: string | null,
): CollateralValueInfo => {
  return {
    unitOfAccount,
    amount,
    number: amount ? Number(amount.toFixed(18)) : null,
    formatted: amount ? amount.toSignificant(4) : null,
    formattedUSD: amount ? formatUSD(Number(amount.toFixed(18))) : null,
    collateralSymbol,
  }
}

/**
 * Converts an array of SingleCollateralValueInfo objects to a Map<Address, CollateralValueInfo>
 * @param collaterals The array of SingleCollateralValueInfo objects
 * @returns A Map<Address, CollateralValueInfo>
 */
export const convertSingleCollateralValueInfoArrayToMap = (
  collaterals: SingleCollateralValueInfo[],
) => {
  return collaterals.reduce((acc, collateral) => {
    acc.set(collateral.collateral, collateral.value)
    return acc
  }, new Map<Address, CollateralValueInfo>())
}

export type CollateralsForLiabilityCurrentVaultV2 = ReturnType<
  typeof selectCollateralsForLiabilityCurrentVaultV2
>
export const selectCollateralsForLiabilityCurrentVaultV2 = createSelector(
  [
    (state: SimulationData) => state.prevTransactionsFirstError,
    (state: SimulationData) => state.currentVaultBefore,
    (state: SimulationData) => state.currentAccountVaultAccountInfoBefore,
    (state: SimulationData) => state.vaultsBefore,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
  ],
  (
    prevTransactionsFirstError,
    liabilityVault,
    _currentAccountVaultAccountInfoBefore,
    vaultsBefore,
    allUnitOfAccountPrices,
  ): {
    error: BatchResultStatus<unknown> | undefined
    shortAssetPrice: CollateralValueInfo
    totalCollateralValueRaw: CollateralValueInfo
    totalCollateralValueBorrowing: CollateralValueInfo
    totalCollateralValueLiquidation: CollateralValueInfo
    collateralsMarketValue: SingleCollateralValueInfo[]
    collateralsBorrowingValue: SingleCollateralValueInfo[]
    collateralsLiquidityValue: SingleCollateralValueInfo[]
    collateralsMarketValueMap: Map<Address, CollateralValueInfo>
    collateralsBorrowingValueMap: Map<Address, CollateralValueInfo>
    collateralsLiquidityValueMap: Map<Address, CollateralValueInfo>
    currentAccountVault?: NonNullable<GetAccountInfoData>["vaultAccountInfo"]
  } => {
    const shortVault = vaultsBefore.get(
      getAddress(liabilityVault?.vault || zeroAddress),
    )
    const liabilityVaultPriceInfo = shortVault?.liabilityPriceInfo
    const shortAssetPrice = selectConvertUnitOfAccountAmountToPrice(
      liabilityVaultPriceInfo?.amountOutMid || 0n,
      shortVault,
      allUnitOfAccountPrices,
    )
    const currentAccountVault = _currentAccountVaultAccountInfoBefore.get(
      getAddress(liabilityVault?.vault || zeroAddress),
    )
    const collateralValueRaw = liabilityVault?.liquidityInfo?.collateralValueRaw
    const collateralValueBorrowing =
      liabilityVault?.liquidityInfo?.collateralValueBorrowing
    const collateralValueLiquidation =
      liabilityVault?.liquidityInfo?.collateralValueLiquidation
    const liabilityVaultFullData = vaultsBefore.get(
      getAddress(liabilityVault?.vault || zeroAddress),
    )

    const collateralValueRawInUSD = selectConvertUnitOfAccountAmountToPrice(
      collateralValueRaw || 0n,
      liabilityVaultFullData,
      allUnitOfAccountPrices,
    )

    const collateralValueBorrowingInUSD =
      selectConvertUnitOfAccountAmountToPrice(
        collateralValueBorrowing || 0n,
        liabilityVaultFullData,
        allUnitOfAccountPrices,
      )
    const collateralValueLiquidationInUSD =
      selectConvertUnitOfAccountAmountToPrice(
        collateralValueLiquidation || 0n,
        liabilityVaultFullData,
        allUnitOfAccountPrices,
      )

    const liabilityVaultCollaterals =
      liabilityVaultFullData?.collateralLTVInfo.reduce(
        (acc, collateral) => ({
          ...acc,
          [collateral.collateral]: {
            borrowLTV: collateral.borrowLTV,
            liquidationLTV: collateral.liquidationLTV,
          },
        }),
        {} as Record<string, { borrowLTV: bigint; liquidationLTV: bigint }>,
      ) || {}

    const collateralsMarketValue: {
      collateral: Address
      value: CollateralValueInfo
    }[] = []
    const collateralsBorrowingValue: {
      collateral: Address
      value: CollateralValueInfo
    }[] = []
    const collateralsLiquidityValue: {
      collateral: Address
      value: CollateralValueInfo
    }[] = []

    for (const [
      i,
      collateral,
    ] of liabilityVault?.liquidityInfo.collaterals.entries() || []) {
      const collateralValueBorrowing =
        liabilityVault?.liquidityInfo.collateralValuesBorrowing[i] || 0n
      const collateralInfo = liabilityVaultCollaterals[collateral]
      if (!collateralInfo) continue
      const collateralVault = vaultsBefore.get(collateral)
      if (!collateralVault) continue
      const collateralValueMarket =
        (collateralValueBorrowing / collateralInfo.borrowLTV) * 10_000n
      const collateralValueMarketPriced =
        selectConvertUnitOfAccountAmountToPrice(
          collateralValueMarket || 0n,
          liabilityVaultFullData,
          allUnitOfAccountPrices,
        )
      const collateralValueBorrowingPriced =
        selectConvertUnitOfAccountAmountToPrice(
          collateralValueBorrowing || 0n,
          liabilityVaultFullData,
          allUnitOfAccountPrices,
        )
      collateralsMarketValue.push({
        collateral,
        value: createCollateralValueInfo(
          collateralValueMarketPriced,
          collateralValueMarket,
          collateralVault?.vaultSymbol,
        ),
      })
      collateralsBorrowingValue.push({
        collateral,
        value: createCollateralValueInfo(
          collateralValueBorrowingPriced,
          collateralValueBorrowing,
          collateralVault?.vaultSymbol,
        ),
      })
    }

    for (const [
      i,
      collateral,
    ] of liabilityVault?.liquidityInfo.collaterals.entries() || []) {
      const collateralValueLiquidation =
        liabilityVault?.liquidityInfo.collateralValuesLiquidation[i] || 0n
      const collateralVault = vaultsBefore.get(collateral)
      const collateralValueLiquidationPriced =
        selectConvertUnitOfAccountAmountToPrice(
          collateralValueLiquidation,
          liabilityVaultFullData,
          allUnitOfAccountPrices,
        )
      collateralsLiquidityValue.push({
        collateral,
        value: createCollateralValueInfo(
          collateralValueLiquidationPriced,
          collateralValueLiquidation,
          collateralVault?.vaultSymbol,
        ),
      })
    }

    return {
      currentAccountVault,
      error: prevTransactionsFirstError,
      shortAssetPrice: createCollateralValueInfo(
        shortAssetPrice,
        liabilityVaultPriceInfo?.amountOutMid || 0n,
      ),
      collateralsMarketValue,
      collateralsBorrowingValue,
      collateralsLiquidityValue,
      totalCollateralValueRaw: createCollateralValueInfo(
        collateralValueRawInUSD,
        collateralValueRaw,
      ),
      totalCollateralValueBorrowing: createCollateralValueInfo(
        collateralValueBorrowingInUSD,
        collateralValueBorrowing,
      ),
      totalCollateralValueLiquidation: createCollateralValueInfo(
        collateralValueLiquidationInUSD,
        collateralValueLiquidation,
      ),
      collateralsMarketValueMap: convertSingleCollateralValueInfoArrayToMap(
        collateralsMarketValue,
      ),
      collateralsBorrowingValueMap: convertSingleCollateralValueInfoArrayToMap(
        collateralsBorrowingValue,
      ),
      collateralsLiquidityValueMap: convertSingleCollateralValueInfoArrayToMap(
        collateralsLiquidityValue,
      ),
    }
  },
)

const _selectBalancesAsAmount = createSelector(
  (balances: ReturnType<typeof selectBalances>) => balances,
  ({
    assetsAccount,
    repayUserMax,
    assetDecimals,
    transferMax,
    vault,
    depositUserMax,
    withdrawUserMax,
    borrowMax,
    borrowVaultMax,
  }) => {
    if (!vault || !assetDecimals) {
      return { assetsAccount: undefined, sharesToAssets: undefined }
    }
    const convertToAmount = (
      value: bigint | undefined,
      vault: Address,
      decimals: bigint | number,
    ) => {
      return value
        ? Amount.fromRawAmount(
            new Token({ address: vault, decimals: Number(decimals) }),
            value,
          )
        : undefined
    }

    return {
      assetsAccount: convertToAmount(assetsAccount, vault, assetDecimals),
      repayUserMax: convertToAmount(repayUserMax, vault, assetDecimals),
      depositUserMax: convertToAmount(depositUserMax, vault, assetDecimals),
      withdrawUserMax: convertToAmount(withdrawUserMax, vault, assetDecimals),
      borrowMax: borrowMax, // Unit of account
      borrowVaultMax: convertToAmount(borrowVaultMax, vault, assetDecimals),
      transferMax: convertToAmount(transferMax, vault, assetDecimals),
    }
  },
)

export const selectBalancesAsAmount = createSelector(
  selectBalances,
  _selectBalancesAsAmount,
)

export const selectBalancesBeforeAsAmount = createSelector(
  [selectBalancesBefore],
  _selectBalancesAsAmount,
)

/// @deprecated
export const selectVaultCheckBeforeAfterAssetsFormatted = createSelector(
  [selectVaultCheckBeforeAfterAssets],
  (data) => {
    if (data.error) {
      return { before: undefined, after: undefined, error: data.error }
    }
    const { before, after, vaultInfo, vaultPrice } = data
    // The select price function needs to be checked as there seems to be a bug in it
    return {
      before: formatUSD(
        selectPrice(before, vaultInfo, vaultPrice || 0n)?.toSignificant(4) || 0,
      ),
      after: formatUSD(
        selectPrice(after, vaultInfo, vaultPrice || 0n)?.toSignificant(4) || 0,
      ),
    }
  },
)

export const selectVaultCheckBeforeAfterAssetsFormattedV2 = createSelector(
  [selectVaultCheckBeforeAfterAssetsV2],
  (data) => {
    if (data.error) {
      return { before: undefined, after: undefined, error: data.error }
    }
    const { before, after, vaultInfo, vaultPrice } = data
    // The select price function needs to be checked as there seems to be a bug in it
    return {
      before: formatUSD(
        selectPrice(before, vaultInfo, vaultPrice || 0n)?.toSignificant(4) || 0,
      ),
      after: formatUSD(
        selectPrice(after, vaultInfo, vaultPrice || 0n)?.toSignificant(4) || 0,
      ),
    }
  },
)

export const selectVaultCheckBeforeAfterBorrowedFormatted = createSelector(
  [selectVaultCheckBeforeAfterBorrowed],
  (data) => {
    if (data.error) {
      return { before: undefined, after: undefined, error: data.error }
    }
    const { before, after, vaultInfo, vaultPrice } = data
    // The select price function needs to be checked as there seems to be a bug in it
    return {
      before: formatUSD(
        selectPrice(before, vaultInfo, vaultPrice || 0n)?.toSignificant(4) || 0,
      ),
      after: formatUSD(
        selectPrice(after, vaultInfo, vaultPrice || 0n)?.toSignificant(4) || 0,
      ),
    }
  },
)

export const selectAccountCollaterals = createSelector(
  [selectCollateralsData, (_, address: Address) => address],
  ({ before, after }, address) => ({
    before: before?.get(getAddress(address)),
    after: after?.get(getAddress(address)),
  }),
)

export const selectVaultCollateral = createSelector(
  [selectAccountCollaterals, (_state, _address, vault: Address) => vault],
  ({ before, after }, vault) => ({
    before: before?.get(getAddress(vault)),
    after: after?.get(getAddress(vault)),
  }),
)

export const selectVaultCollateralFormatted = createSelector(
  [selectVaultCollateral],
  (collateralValue): FormattedFieldResult => {
    if (!collateralValue)
      return {
        from: undefined,
        fromType: undefined,
        to: undefined,
        toType: undefined,
      }
    const fromFormatted = collateralValue.before
      ? formatBigIntToPrice(collateralValue.before.collateralValueBorrowing, 18)
      : undefined
    const toFormatted = collateralValue.after
      ? formatBigIntToPrice(collateralValue.after.collateralValueBorrowing, 18)
      : undefined

    return {
      from: collateralValue.before?.queryFailure ? QUERY_FAILED : fromFormatted,
      fromType: collateralValue.before?.queryFailure ? TYPE_ERROR : TYPE_INFO,
      to: collateralValue.after?.queryFailure ? QUERY_FAILED : toFormatted,
      toType: collateralValue.after?.queryFailure ? TYPE_ERROR : TYPE_INFO,
    }
  },
)

export const calcualteSafeLTV = (ltv: bigint): bigint => {
  const ltvNumber = Number(ltv)
  // This calculation ensures that if we borrow the 100% our HF is near 1.1
  const a = (ltvNumber * 10) / 11
  const calculation = Math.round(a)

  return BigInt(calculation)
}

const calculateMaxBorrowAmountInUnitOfAccount = (
  vaultData: GetVaultData,
  vaultCheckResult: SimulationData["currentVaultBefore"],
): bigint => {
  let collateralPower = 0n
  if (!vaultCheckResult?.liquidityInfo?.collaterals?.length) {
    return collateralPower
  }

  const controllers = vaultData?.collateralLTVInfo

  if (!controllers || controllers.length === 0) return 0n

  for (const [
    i,
    collateral,
  ] of vaultCheckResult.liquidityInfo.collaterals.entries() || []) {
    const collateralValue =
      vaultCheckResult.liquidityInfo.collateralValuesRaw[i] || 0n
    // Get collateral information
    const collateralInfo = controllers.find((controller) =>
      isAddressEqual(controller.collateral, collateral),
    )
    if (!collateralInfo) continue
    // Calculate the value allowed by the collateral
    // Collateral * 10_000 / LTV
    const safeLTV = calcualteSafeLTV(collateralInfo.borrowLTV)
    collateralPower += (collateralValue / 10_000n) * safeLTV
  }

  if (!collateralPower) return 0n
  const debtValue = vaultCheckResult.liquidityInfo.liabilityValueBorrowing
  // Borrow Power in unitof account
  return collateralPower > debtValue ? collateralPower - debtValue : 0n
}

const calculateLtv = (
  vaultsData: SimulationData["vaultsBefore"],
  vaultCheckResult: SimulationData["currentVaultBefore"],
  borrowLTV = true,
): bigint => {
  let collateralValue = 0n
  if (!vaultCheckResult?.liquidityInfo?.collaterals?.length)
    return collateralValue
  for (const [
    i,
    collateral,
  ] of vaultCheckResult.liquidityInfo.collaterals.entries() || []) {
    const _collateralValue =
      vaultCheckResult.liquidityInfo.collateralValuesBorrowing[i] || 0n
    const controllerInfo = vaultsData
      .get(getAddress(vaultCheckResult.vault))
      ?.collateralLTVInfo.find((c) => isAddressEqual(c.collateral, collateral))
    if (!controllerInfo) continue
    collateralValue +=
      (_collateralValue * 10_000n) /
      (borrowLTV ? controllerInfo.borrowLTV : controllerInfo.liquidationLTV)
  }

  if (!collateralValue) return 0n

  return (
    (vaultCheckResult.liquidityInfo.liabilityValueBorrowing * 10n ** 18n) /
    collateralValue
  )
}

export const selectYourLTV = createSelector(
  [
    (state: SimulationData) => state.currentVaultBefore,
    (state: SimulationData) => state.currentVaultAfter,
    (state: SimulationData) => state.vaultsBefore,
    (state: SimulationData) => state.vaultsAfter,
  ],
  (vaultCheckBefore, vaultCheckAfter, vaultsBefore, vaultsAfter) => {
    const yourLTVBefore = calculateLtv(vaultsBefore, vaultCheckBefore)
    const yourLTVAfter = calculateLtv(vaultsAfter, vaultCheckAfter)
    const yourLTVBeforeFormatted = formatPercent(formatUnits(yourLTVBefore, 18))
    const yourLTVAfterFormatted = formatPercent(formatUnits(yourLTVAfter, 18))
    return {
      before: yourLTVBeforeFormatted,
      after: yourLTVAfterFormatted,
    }
  },
)

export const selectLiquidationLTV = createSelector(
  [
    (state: SimulationData) => state.currentVaultBefore,
    (state: SimulationData) => state.currentVaultAfter,
    (state: SimulationData) => state.vaultsBefore,
    (state: SimulationData) => state.vaultsAfter,
  ],
  (vaultCheckBefore, vaultCheckAfter, vaultsBefore, vaultsAfter) => {
    const yourLTVBefore = calculateLtv(vaultsBefore, vaultCheckBefore, false)
    const yourLTVAfter = calculateLtv(vaultsAfter, vaultCheckAfter, false)
    const yourLTVBeforeFormatted = formatPercent(formatUnits(yourLTVBefore, 18))
    const yourLTVAfterFormatted = formatPercent(formatUnits(yourLTVAfter, 18))
    return {
      before: yourLTVBeforeFormatted,
      after: yourLTVAfterFormatted,
    }
  },
)
export const selectLiabilitiesData = createSelector(
  [(simulation: SimulationData) => simulation],
  (simulation) => ({
    before: simulation?.liabilityValueBefore,
    after: simulation?.liabilityValueAfter,
  }),
)

export const selectAccountLiabilityValues = createSelector(
  [selectLiabilitiesData, (_, address: Address) => address],
  ({ before, after }, address) => ({
    before: before?.get(getAddress(address)),
    after: after?.get(getAddress(address)),
  }),
)

export const selectVaultLiabilityValues = createSelector(
  [selectAccountLiabilityValues, (_state, _address, vault: Address) => vault],
  ({ before, after }, vault) => ({
    before: before?.get(getAddress(vault)),
    after: after?.get(getAddress(vault)),
    vault,
  }),
)

export const selectVaultLiabilityValuePrice = createSelector(
  [
    selectVaultLiabilityValues,
    (_state, _address, _vault, allUnitOfAccountPrices: Map<Address, bigint>) =>
      allUnitOfAccountPrices,
    (state: SimulationData) => state,
  ],
  (liabilityValue, allUnitOfAccountPrices, simulation) => {
    const vault = liabilityValue.vault
    const vaultInfo = simulation?.vaultsAfter?.get(getAddress(vault))
    if (!vaultInfo)
      return { vaultPrice: undefined, vaultInfo, error: "No vault info" }
    if (!vault) return { vaultPrice: undefined, vaultInfo, error: "No vault" }
    const vaultPrice = allUnitOfAccountPrices?.get(
      getAddress(
        vaultInfo.unitOfAccount === zeroAddress
          ? vaultInfo.backupAssetPriceInfo.unitOfAccount
          : vaultInfo.unitOfAccount,
      ),
    )
    if (!vaultPrice || vaultPrice === 0n) {
      return { vaultPrice: undefined, vaultInfo, error: "No vault price" }
    }
    return { vaultPrice, vaultInfo, error: undefined }
  },
)

export const selectVaultLiabilityValuesFormatted = createSelector(
  [selectVaultLiabilityValues, selectVaultLiabilityValuePrice],
  (liabilityValue, { vaultPrice, error }): FormattedFieldResult => {
    if (error)
      return {
        from: error,
        fromType: TYPE_ERROR,
        to: error,
        toType: TYPE_ERROR,
      }

    const priceBefore =
      (liabilityValue.before?.liabilityValue || 0n) * (vaultPrice || 0n)

    const priceAfter =
      (liabilityValue.after?.liabilityValue || 0n) * (vaultPrice || 0n)

    // 18 decimals liability value and 8 decimals vault price
    const fromFormatted = formatBigIntToPrice(priceBefore || 0n, 26)
    const toFormatted = formatBigIntToPrice(priceAfter || 0n, 26)

    return {
      from: liabilityValue.before?.queryFailure ? QUERY_FAILED : fromFormatted,
      fromType: liabilityValue.before?.queryFailure ? TYPE_ERROR : TYPE_INFO,
      to: liabilityValue.after?.queryFailure ? QUERY_FAILED : toFormatted,
      toType: liabilityValue.after?.queryFailure ? TYPE_ERROR : TYPE_INFO,
    }
  },
)

export const selectAccountsHealth = createSelector(
  [(simulation: SimulationData) => simulation],
  (simulation) => {
    const health = new Map<Address, ReturnType<typeof selectAccountHealth>>()
    for (const address of simulation.receivers) {
      health.set(
        getAddress(address),
        selectAccountHealth(simulation, getAddress(address)),
      )
    }

    return health
  },
)

export const selectVaultsData = createSelector(
  [(simulation: SimulationData) => simulation],
  (simulation) => ({
    before: simulation?.vaultsBefore,
    after: simulation?.vaultsAfter,
  }),
)

export const selectVaultsDataByAddress = createSelector(
  [selectVaultsData, (_, address: Address) => address],
  ({ before, after }, address) => ({
    before: before?.get(getAddress(address)),
    after: after?.get(getAddress(address)),
  }),
)

export const selectVaultInterestRateInfo = createSelector(
  [selectVaultsDataByAddress],
  ({ before, after }) => ({
    before: before?.irmInfo.interestRateInfo[0],
    after: after?.irmInfo.interestRateInfo[0],
  }),
)

export const selectVaultSupplyAPY = createSelector(
  [selectVaultInterestRateInfo],
  ({ before, after }) => ({
    before: before?.supplyAPY,
    after: after?.supplyAPY,
  }),
)

export const selectVaultBorrowAPY = createSelector(
  [selectVaultInterestRateInfo],
  ({ before, after }) => ({
    before: before?.borrowAPY,
    after: after?.borrowAPY,
  }),
)

export const selectVaultSupplyAPYPercentage = createSelector(
  [selectVaultSupplyAPY],
  (data) => {
    const { before, after } = data
    const beforePercentage = before
      ? new Percent(before || "0", 10n ** 27n).toPercentageString(2)
      : ZERO_PERCENT
    const afterPercentage = after
      ? new Percent(after || "0", 10n ** 27n).toPercentageString(2)
      : ZERO_PERCENT
    return {
      before: beforePercentage,
      after: afterPercentage,
    }
  },
)

export const selectVaultBorrowAPYPercentage = createSelector(
  [selectVaultBorrowAPY],
  ({ before, after }) => {
    const beforePercentage = before
      ? new Percent(before || "0", 10n ** 27n).toPercentageString(2)
      : ZERO_PERCENT
    const afterPercentage = after
      ? new Percent(after || "0", 10n ** 27n).toPercentageString(2)
      : ZERO_PERCENT
    return {
      before: beforePercentage,
      after: afterPercentage,
    }
  },
)

export const selectSimulatedVaults = (simulation: SimulationData) =>
  simulation.vaults.filter(nonNullable)

export const selectSimulatedVault = createSelector(
  [
    (vault: GetVaultData) => vault,
    (_, simulatedVaults?: GetVaultData[]) => simulatedVaults,
  ],
  (vault, simulatedVaults) => {
    const sim = simulatedVaults?.find(
      (v) => v && vault && getAddress(v.vault) === getAddress(vault.vault),
    )
    if (sim) {
      if (!vault) return sim
      return {
        ...sim,
        vaultSymbol: vault.vaultSymbol,
        vaultName: vault.vaultName,
        unitOfAccount: vault.unitOfAccount,
        nameOverride: vault.nameOverride,
        vaultCreator: vault.vaultCreator,
        governorAdmin: vault.governorAdmin,
      }
    }

    return vault
  },
)

export const selectSimulatedBalances = (simulation: SimulationData) =>
  simulation.balancesAfter

export const selectSimulatedBalancesBefore = (simulation: SimulationData) =>
  simulation.balancesBefore

export const selectSimulatedAccountInfos = (simulation: SimulationData) =>
  simulation.accountInfosAfter

export const selectSimulatedAccountInfosBefore = (simulation: SimulationData) =>
  simulation.accountInfosBefore

export const replaceAccountInfo = createSelector(
  [
    (simulatedAccounts?: GetAccountInfoData[]) => simulatedAccounts,
    (_, balances: Map<Address, bigint>) => balances,
    (_, __, accountInfo: GetAccountInfoData) => accountInfo,
  ],
  (simulatedAccounts, balances, accountInfo): GetAccountInfoData => {
    /// todo: refactor simulation to use mapping not arrays
    const simulatedAccount =
      simulatedAccounts?.find(
        (account) =>
          account &&
          accountInfo &&
          getAddress(account.evcAccountInfo.account) ===
            getAddress(accountInfo.evcAccountInfo.account) &&
          getAddress(account.vaultAccountInfo.vault) ===
            getAddress(accountInfo.vaultAccountInfo.vault),
      ) || accountInfo

    if (!simulatedAccount) return accountInfo

    const balance = balances.get(
      getAddress(simulatedAccount.vaultAccountInfo.asset),
    )

    const assetsAccount =
      balance !== undefined
        ? balance
        : simulatedAccount.vaultAccountInfo.assetsAccount
    return {
      ...simulatedAccount,
      vaultAccountInfo: {
        ...simulatedAccount.vaultAccountInfo,
        assetsAccount,
      },
    }
  },
)

export const selectSimulatedAccountInfo = createSelector(
  [
    selectSimulatedAccountInfos,
    selectSimulatedBalances,
    (_, accountInfo: GetAccountInfoData) => accountInfo,
  ],
  (simulatedAccounts, balances, accountInfo): GetAccountInfoData => {
    return replaceAccountInfo(simulatedAccounts, balances, accountInfo)
  },
)

export const selectSimulatedAccountInfoBefore = createSelector(
  [
    selectSimulatedAccountInfosBefore,
    selectSimulatedBalancesBefore,
    (_, accountInfo: GetAccountInfoData) => accountInfo,
  ],
  (simulatedAccounts, balances, accountInfo): GetAccountInfoData => {
    return replaceAccountInfo(simulatedAccounts, balances, accountInfo)
  },
)
